import React, { useEffect, useState } from "react";
import {
  PartialCastForm,
  PartialCastProps,
} from "../../../types/UploadFieldsType";
import TextInput from "../formComp/TextInputNew";
import SelectInput from "../formComp/SelectInputNew";
import ToothField from "../formComp/ToothField";

const PartialCast: React.FC<PartialCastProps> = ({ onChange }) => {
  const [form, setForm] = useState<PartialCastForm>({
    frameworkMaterial: "",
    desiredArch: "",
    majorConnectorUpperDesignType: "",
    majorConnectorUpperCreateWindow: "",
    majorConnectorLower: "",
    minorConnector: "",
    directRetainerTeeth: [],

    directRetainerClasp: "",

    indirectRetainer: "",
    rest: "",
    additionalNotes: "",
  });

  const handleToothClick = (type: "directRetainerTeeth", tooth: number) => {
    const toothStr = tooth.toString();

    setForm((prev) => {
      const updated = prev[type].includes(toothStr)
        ? prev[type].filter((t) => t !== toothStr)
        : [...prev[type], toothStr];
      return { ...prev, [type]: updated };
    });
  };

  const handleChange = (field: keyof PartialCastForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    onChange(form);
  }, [form, onChange]);

  const checkboxOptions = [
    "I Bar",
    "C Clasp",
    "Embrasure Clasp",
    "Full arch",
    "Other (Please mention details in *Additional Notes* below)",
  ];
  const selectedPreferences = form.directRetainerClasp
    ? form.directRetainerClasp.split(",").map((s) => s.trim())
    : [];

  return (
    <div className="w-full text-[var(--cu-text)]">
      <div className="mb-3 border-b border-[var(--cu-border)] pb-2">
        <h2 className="text-sm font-medium text-[var(--cu-accent)]">
          RPD Framework
        </h2>
      </div>

      <div className="w-full space-y-3">
        {/* Framework material */}
        <SelectInput
          label="Framework material"
          name="frameworkMaterial"
          value={form.frameworkMaterial}
          onChange={(e) => handleChange("frameworkMaterial", e.target.value)}
          options={[
            { value: "Co-Cr", label: "Co-Cr" },
            { value: "Peek / Composite", label: "Peek / Composite" },
          ]}
        />

        {/* Desired arch */}
        <SelectInput
          label="Desired arch"
          name="desiredArch"
          value={form.desiredArch}
          onChange={(e) => handleChange("desiredArch", e.target.value)}
          options={[
            { value: "Upper", label: "Upper" },
            { value: "Lower", label: "Lower" },
            { value: "Upper and Lower", label: "Upper and Lower" },
          ]}
        />

        {/* [Upper only] Major Connector - Design Type */}
        <SelectInput
          optional
          label="[Upper only] Major Connector - Design Type"
          name="majorConnectorUpperDesignType"
          value={form.majorConnectorUpperDesignType}
          onChange={(e) =>
            handleChange("majorConnectorUpperDesignType", e.target.value)
          }
          options={[
            { value: "Complete Palate", label: "Complete Palate" },
            { value: "Palatal Strap", label: "Palatal Strap" },
            { value: "Horseshoe", label: "Horseshoe" },
            {
              value: "Other",
              label:
                "Other(please mention details in *Additional Notes* below)",
            },
          ]}
        />

        {/* [Upper only] Major Connector - Create window */}
        <SelectInput
          optional
          label="[Upper only] Major Connector - Create window"
          name="majorConnectorUpperCreateWindow"
          value={form.majorConnectorUpperCreateWindow}
          onChange={(e) =>
            handleChange("majorConnectorUpperCreateWindow", e.target.value)
          }
          options={[
            { value: "Yes", label: "Yes" },
            { value: "No", label: "No" },
          ]}
        />

        {/* [Lower only] Major Connector */}
        <SelectInput
          optional
          label="[Lower only] Major Connector"
          name="majorConnectorLower"
          value={form.majorConnectorLower}
          onChange={(e) => handleChange("majorConnectorLower", e.target.value)}
          options={[
            { value: "Lingual Bar", label: "Lingual Bar" },
            { value: "Lingual Plate", label: "Lingual Plate" },
            {
              value: "Other",
              label:
                "Other(please mention details in *Additional Notes* below)",
            },
          ]}
        />

        {/* Minor connector */}
        <TextInput
          optional
          label="Minor connector"
          name="minorConnector"
          value={form.minorConnector}
          onChange={(e) => handleChange("minorConnector", e.target.value)}
        />

        <ToothField
          label="Direct retainer — Teeth to be clasped"
          optional
          toothNumbers={form.directRetainerTeeth}
          onToothClick={(tooth) =>
            handleToothClick("directRetainerTeeth", tooth)
          }
        />

        <div className="mb-5">
          <label className="mb-2 block text-sm font-medium text-[var(--cu-muted)]">
            Direct retainer — Preferred clasp design
            <span className="ml-2 text-[9px] font-medium normal-case tracking-normal text-[var(--cu-subtle)]">
              Optional
            </span>
          </label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {checkboxOptions.map((option) => {
              const isChecked = selectedPreferences.includes(option);

              return (
                <label
                  key={option}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-xl border px-3.5 py-3 text-sm transition-all duration-300 ${
                    isChecked
                      ? "border-[var(--cu-accent)]/50 bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
                      : "border-[var(--cu-border)] bg-[var(--cu-input)] text-[var(--cu-muted)] hover:border-[var(--cu-accent)]/40"
                  }`}
                >
                  <input
                    type="checkbox"
                    value={option}
                    checked={isChecked}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      let updatedPreferences: string[];

                      if (checked) {
                        updatedPreferences = [...selectedPreferences, option];
                      } else {
                        updatedPreferences = selectedPreferences.filter(
                          (item) => item !== option
                        );
                      }

                      setForm((prev) => ({
                        ...prev,
                        directRetainerClasp: updatedPreferences.join(", "),
                      }));
                    }}
                    className="mt-0.5 accent-[#2fa9bf]"
                  />
                  <span className="leading-snug">{option}</span>
                </label>
              );
            })}
          </div>
        </div>
        {/* Indirect retainer */}
        <TextInput
          optional
          label="Indirect retainer"
          name="indirectRetainer"
          value={form.indirectRetainer}
          onChange={(e) => handleChange("indirectRetainer", e.target.value)}
        />

        {/* Rest */}
        <TextInput
          optional
          label="Rest"
          name="rest"
          value={form.rest}
          onChange={(e) => handleChange("rest", e.target.value)}
        />

        {/* Additional notes */}
        <TextInput
          optional
          label="Additional notes"
          name="additionalNotes"
          value={form.additionalNotes}
          onChange={(e) => handleChange("additionalNotes", e.target.value)}
        />
      </div>
    </div>
  );
};

export default PartialCast;
